import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useHistory, useParams } from "react-router-dom";
import theme from "styles/theme.json";
import { ROUTE_PATH } from "utils/constants";
import { findOptionByValue, missionTypeFilterOptions, perPageOptions } from "utils/constants/options";
import { parseQueryString } from "utils/functions/parseQueryString";

import HeaderWithActions from "components/Displays/HeaderWithActions";
import {
  BoxDeleteConfirm,
  Buttons,
  Displays,
  Icons,
  Inputs,
  Label,
  Loading,
  Modal,
  PaginationNormal,
  StatusTag,
  TableCustom,
} from "components";
import { adminService } from "apiServices";
import { TeamManagementContainerStyled } from "./styled";
import { toast } from "react-toastify";
import { formatDisplayNumber } from "utils/functions/number";


const TeamManagementContainer = () => {
  const { id } = useParams();
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [teams, setTeams] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isShowDeleteModal, setIsShowDeleteModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const fetchData = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    const stringQuery = parseQueryString({
      per_page: pageSize,
      page: currentPage,
    });
    const res = await adminService.GET_MISSION_V2_TEAMS(id, stringQuery);
    if (res?.success && res?.data) {
      const { teams: teamList, ...missionData } = res.data;
      setData(missionData);
      setTeams(Array.isArray(teamList) ? teamList : []);
      setPagination(res?.page ? { ...res.page } : null);
    } else {
      setData(null);
      setTeams([]);
      setPagination(null);
      toast.error(
        <Displays.MsgToast
          theme_error_toast
          labelTop="ไม่สามารถโหลดข้อมูลทีมได้"
          labelBottom={'มีข้อผิดพลาดบางอย่าง กรุณาลองใหม่อีกครั้ง'}
        />
      );
    }
    setIsLoading(false);
  }, [id, currentPage, pageSize]);

  const handleBack = useCallback(() => {
    history.push(
      ROUTE_PATH.BACK_OFFICE_COMPANY_MISSION_V2_DETAIL.replace(":id", id)
    );
  }, [history, id]);

  const handleDeleteTeam = useCallback(async () => {
    if (!selectedTeam || !id) return;
    setIsShowDeleteModal(false);
    const res = await adminService.DELETE_MISSION_V2_TEAM(
      id,
      selectedTeam.mission_team_id
    );
    if (res?.success) {
      setSelectedTeam(null);
      toast.success(
        <Displays.MsgToast theme_success_toast labelTop="ลบทีมสำเร็จ" />
      );
      await fetchData();
    } else {
      toast.error(
        <Displays.MsgToast
          theme_error_toast
          labelTop="ลบทีมไม่สำเร็จ"
          labelBottom={'มีข้อผิดพลาดบางอย่าง กรุณาลองใหม่อีกครั้ง'}
        />
      );
    }
  }, [selectedTeam, id, fetchData]);

  const handleOpenDeleteModal = useCallback((team) => {
    setSelectedTeam(team);
    setIsShowDeleteModal(true);
  }, []);

  const handleCloseModel = useCallback(() => {
    setIsShowDeleteModal(false);
    setSelectedTeam(null);
  }, []);

  const maxMembers = data?.team_size || 1;

  const columns = useMemo(() => {
    return [
      {
        title: "ลำดับ",
        field: "no",
        headerStyle: { textAlign: "left", width: "5%" },
        style: { textAlign: "left", width: "5%" },

      },
      {
        title: "ชื่อทีม",
        field: "team_display_name",
        headerStyle: { textAlign: "left", width: "40%" },
        style: { textAlign: "left", width: "40%" },
      },
      {
        title: "จำนวนสมาชิก",
        field: "member_count",
        headerStyle: { textAlign: "left", width: "40%" },
        style: { textAlign: "left", width: "40%" },
        render: ({ value }) => {
          const pct = (value / maxMembers) * 100;
          return (
            <div className="member_cell">
              <div className="progress_wrap">
                <Displays.MissionProgressBar percent={pct} height={12} />
              </div>
              <span className="member_count_text">
                <span className="member_count_value">{value}</span> / {maxMembers} คน
              </span>
            </div>
          );
        },
      },
      {
        title: "",
        field: "actions",
        headerStyle: { textAlign: "center", width: "10%" },
        style: { textAlign: "center", width: "10%" },
        render: ({ origin }) => (
          <div style={{ width: '95px' }}>
            <Buttons.BtnNormal
              theme_only_border_red
              svg_front={<Icons.Delete5 />}
              label="ลบทีม"
              borderRadius="8px"
              padding="2px"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDeleteModal(origin);
              }}
            />
          </div>
        ),
      },
    ];
  }, [maxMembers, handleOpenDeleteModal]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);


  return (
    <TeamManagementContainerStyled>
      <div className="content_section">
        {isLoading ? (
          <div className="loading_wrap">
            <Loading theme_standard_loading />
          </div>
        ) : (
          <>
            <div className="header_block">
              <HeaderWithActions
                theme_admin
                handleBackCustom={handleBack}
                title="จัดการทีม"
                subTitle="Team Management"
              />
            </div>
            <div className="mission_info_row">
              <div>
                <span className="code_label">รหัสภารกิจ :</span>
                <span className="code_value">{data?.mission_code ?? "-"}</span>
              </div>
              <div className="status_area">
                <span className="status_label">สถานะ :</span>
                <StatusTag
                  variant="company_mission"
                  status={data?.status}
                />
              </div>
            </div>

            <div className="mission_fields_row">
              <div className="field_item">
                <Label.LabelForm
                  label="ชื่อการกิจ (TH)"
                  subLabel="Mission Title (TH)"
                  required
                />
                <Inputs.InputText
                  theme_standard_new
                  disabled
                  value={data?.name_th ?? ""}
                />
              </div>
              <div className="field_item">
                <Label.LabelForm
                  label="ประเภทภารกิจ"
                  subLabel="Mission Type"
                  required
                />
                <Inputs.AutoSuggestDropdown
                  theme_autosugget
                  disabled
                  value={findOptionByValue(missionTypeFilterOptions, data?.category)}
                  options={missionTypeFilterOptions}
                  iconColor={theme.COLORS.PURPLE_1}
                />
              </div>
            </div>

            <div className="team_section_header">
              <div className="team_section_title">รายละเอียดทีม</div>
              <div className="team_summary">
                ทั้งหมดรวม
                <span className="team_summary_number">
                  {formatDisplayNumber(data?.summary?.total_teams ?? 0)} ทีม
                </span>
                <span className="team_summary_separator">|</span>
                <span className="team_summary_number">
                  {formatDisplayNumber(data?.summary?.total_members_in_teams ?? 0)} คน
                </span>
              </div>
            </div>

            <div className="table_section">
              <TableCustom.TableNormal
                theme_table_new
                useNo
                useRenderNoPage
                pagination={pagination}
                columns={columns}
                data={teams}
                showPagination={false}
                isUseResetSelect={false}
              />
            </div>

          </>
        )}
      </div>

      <div className="pagination_section">
        <PaginationNormal
          currentPage={pagination?.current_page || 1}
          fromOrder={pagination?.from || 0}
          toOrder={pagination?.to || 0}
          perPage={pagination?.per_page || 20}
          totalPage={pagination?.total || 0}
          lastPage={pagination?.last_page || 1}
          onChangePerPage={(val) => {
            setPageSize(val);
            setCurrentPage(1);
          }}
          onClickPage={(page) => setCurrentPage(page)}
          widthbox="72px"
          perPageOptions={perPageOptions}
          alwaysShowArrows
        />
      </div>

      <Modal
        theme_modal_standard
        isShow={isShowDeleteModal}
        handleClickCloseModal={handleCloseModel}
      >
        <BoxDeleteConfirm
          handleCancel={handleCloseModel}
          handleConfirm={handleDeleteTeam}
          iconPrefixTitle={<Icons.Delete5 width="32" height="32" />}
          textShow="ยืนยันการลบทีมนี้หรือไม่ ?"
          textBottom={`คุณต้องการลบทีม ${selectedTeam?.team_display_name ?? ""}\nมีจำนวนสมาชิก ${selectedTeam?.member_count ?? 0} คน`}
        />
      </Modal>
    </TeamManagementContainerStyled>
  );
};

export default TeamManagementContainer;
